// ══════════════════════════════════════════════════════════════
// Phonics Scope & Sequence
// Based on UFLI Group 1 order, cross-checked with Fundations K
// ══════════════════════════════════════════════════════════════

import { shuffle } from "../util/random.js";

export const LETTER_ORDER = [
  "a","m","s","t","p","f","i","n","d","o",
  "c","k","u","b","g","e","h","r","l","w",
  "j","y","x","v","z",
];

export const MICROSETS = [
  { id: "R0", letters: ["a","m","s","t"], label: "First letters", purpose: "First vowel + high-utility consonants; unlocks sat, mat, tap" },
  { id: "R1", letters: ["p","f"], label: "More consonants", purpose: "Adds consonants while keeping only short-a; more CVC practice before second vowel" },
  { id: "R2", letters: ["i","n"], label: "Short i + n", purpose: "Second short vowel and nasal; creates short-a/short-i CVC contrasts" },
  { id: "R3", letters: ["d","o","c","k"], label: "Short o + /k/", purpose: "Third short vowel and /k/ spelling; expands word set with regular correspondences" },
  { id: "R4", letters: ["u","b","g","e"], label: "All short vowels", purpose: "Remaining short vowels and common consonants; prepares for mixed review" },
  { id: "R5", letters: ["h","r","l","w","j","y","x","v","z"], label: "Later consonants", purpose: "Lower-frequency consonants and alternate sounds" },
  { id: "R6", letters: [], digraphs: ["sh","ch","th"], label: "Digraphs", purpose: "First consonant digraphs (sh/ch/th) — a new grapheme-phoneme dimension the single-letter ladder never granted (ADR-070 OQ-1); taught via reading.11" },
];

export const LETTER_SOUNDS = {
  a: { phoneme: "/ă/", keyword: "apple" },
  m: { phoneme: "/m/", keyword: "map" },
  s: { phoneme: "/s/", keyword: "sun" },
  t: { phoneme: "/t/", keyword: "top" },
  p: { phoneme: "/p/", keyword: "pan" },
  f: { phoneme: "/f/", keyword: "fan" },
  i: { phoneme: "/ĭ/", keyword: "itch" },
  n: { phoneme: "/n/", keyword: "net" },
  d: { phoneme: "/d/", keyword: "dog" },
  o: { phoneme: "/ŏ/", keyword: "octopus" },
  c: { phoneme: "/k/", keyword: "cat" },
  k: { phoneme: "/k/", keyword: "kit" },
  u: { phoneme: "/ŭ/", keyword: "up" },
  b: { phoneme: "/b/", keyword: "bat" },
  g: { phoneme: "/g/", keyword: "got" },
  e: { phoneme: "/ĕ/", keyword: "egg" },
  h: { phoneme: "/h/", keyword: "hat" },
  r: { phoneme: "/r/", keyword: "run" },
  l: { phoneme: "/l/", keyword: "lip" },
  w: { phoneme: "/w/", keyword: "wet" },
  j: { phoneme: "/j/", keyword: "jam" },
  y: { phoneme: "/y/", keyword: "yes" },
  x: { phoneme: "/ks/", keyword: "fox" },
  v: { phoneme: "/v/", keyword: "van" },
  z: { phoneme: "/z/", keyword: "zip" },
};

const VISUAL_CONFUSIONS = {
  m: ["n","w","u"], n: ["m","h","r"], b: ["d","p","q"], d: ["b","p","q"],
  p: ["b","d","q"], q: ["b","d","p"], s: ["z","c","5"], a: ["o","d","e"],
  e: ["a","c","o"], u: ["n","v","w"], t: ["f","l","+"], f: ["t","l"],
  i: ["l","j","1"], g: ["q","y","9"], c: ["o","e","a"], k: ["x","h"],
};

export function lettersKnownAt(setId) {
  const result = [];
  for (const ms of MICROSETS) {
    result.push(...ms.letters);
    if (ms.id === setId) break;
  }
  return result;
}

export function microsetForLetter(letter) {
  for (const ms of MICROSETS) {
    if (ms.letters.includes(letter)) return ms.id;
  }
  return null;
}

/** Cumulative digraphs known through (and including) `setId` — mirrors
 *  lettersKnownAt but for the parallel digraph dimension (ADR-070 OQ-1). */
export function digraphsKnownAt(setId) {
  const result = [];
  for (const ms of MICROSETS) {
    result.push(...(ms.digraphs || []));
    if (ms.id === setId) break;
  }
  return result;
}

export function letterDistractors(letter, knownLetters, count = 3) {
  const confusions = (VISUAL_CONFUSIONS[letter] || []).filter(
    (l) => knownLetters.includes(l) && l !== letter
  );
  const others = knownLetters.filter(
    (l) => l !== letter && !confusions.includes(l)
  );
  const pool = [...confusions, ...shuffle(others)];
  return pool.slice(0, count);
}
